import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminApi } from '../../lib/http';
import {
  PortalButton,
  PortalCard,
  PortalEmpty,
  PortalInput,
  PortalModal,
  PortalSelect,
  PortalStat,
  PortalStatus,
  PortalTextarea,
} from '../../components/portal/PortalElements';

function lastSevenDays(bookings) {
  const points = [];
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  for (let index = 6; index >= 0; index -= 1) {
    const day = new Date(today);
    day.setUTCDate(today.getUTCDate() - index);
    const iso = day.toISOString().slice(0, 10);
    const dayBookings = bookings.filter((booking) => booking.date === iso);
    points.push({
      date: iso,
      label: day.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      }),
      bookings: dayBookings.length,
      revenue: dayBookings
        .filter((booking) => booking.paymentStatus === 'paid')
        .reduce((sum, booking) => sum + Number(booking.totalAmount || 0), 0),
    });
  }

  return points;
}

function paymentLabel(booking) {
  return booking.paymentStatus === 'paid' ? `Paid - Rs. ${booking.totalAmount || 0}` : 'Pay at venue';
}

function TrendChart({ title, points, valueKey, stroke, fill }) {
  const chartPadding = {
    top: 12,
    right: 12,
    bottom: 12,
    left: 52,
  };
  const maxValue = Math.max(...points.map((point) => point[valueKey]), 1);
  const chartWidth = 612;
  const chartHeight = 180;
  const plotWidth = chartWidth - chartPadding.left - chartPadding.right;
  const plotHeight = chartHeight - chartPadding.top - chartPadding.bottom;
  const stepX = plotWidth / Math.max(points.length - 1, 1);
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => {
    const value = Math.round(maxValue * (1 - ratio));
    const y = chartPadding.top + plotHeight * ratio;
    return { ratio, value, y };
  });
  const pathPoints = points.map((point, index) => {
    const value = point[valueKey];
    const x = chartPadding.left + index * stepX;
    const y = chartPadding.top + plotHeight - (value / maxValue) * plotHeight;
    return { x, y, value, label: point.label, date: point.date };
  });
  const linePath = pathPoints
    .map((point, index) =>
      `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`,
    )
    .join(' ');
  const areaPath = `${linePath} L ${chartPadding.left + plotWidth} ${chartPadding.top + plotHeight} L ${chartPadding.left} ${chartPadding.top + plotHeight} Z`;

  return (
    <PortalCard>
      <div className="portal-section-head">
        <div>
          <h2 className="portal-chart-title">{title}</h2>
        </div>
      </div>
      <div className="portal-chart-shell">
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight + 24}`}
          className="portal-chart-svg"
          preserveAspectRatio="none"
        >
          {yTicks.map((tick) => {
            return (
              <g key={`${valueKey}-${tick.ratio}`}>
                <line
                  x1={chartPadding.left}
                  y1={tick.y}
                  x2={chartPadding.left + plotWidth}
                  y2={tick.y}
                  stroke="#ead7c9"
                  strokeDasharray="4 6"
                />
                <text
                  x={chartPadding.left - 10}
                  y={tick.y + 4}
                  textAnchor="end"
                  className="portal-chart-axis-text"
                >
                  {valueKey === 'revenue' ? `Rs. ${tick.value}` : tick.value}
                </text>
              </g>
            );
          })}
          <path d={areaPath} fill={fill} opacity="0.25" />
          <path
            d={linePath}
            fill="none"
            stroke={stroke}
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {pathPoints.map((point) => (
            <g key={`${valueKey}-${point.date}`}>
              <circle cx={point.x} cy={point.y} r="5" fill={stroke} />
              <circle cx={point.x} cy={point.y} r="10" fill={stroke} opacity="0.14" />
            </g>
          ))}
        </svg>
        <div className="portal-chart-labels">
          {pathPoints.map((point) => (
            <div key={`${valueKey}-${point.date}-label`} className="portal-chart-label">
              <div className="portal-chart-date">{point.label}</div>
            </div>
          ))}
        </div>
      </div>
    </PortalCard>
  );
}

export default function AdminDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    tablePreference: '',
    paymentStatus: 'pending',
    couponCode: '',
    specialRequest: '',
  });

  async function loadDashboard() {
    setLoading(true);
    try {
      const [dashboardResponse, bookingsResponse] = await Promise.all([
        adminApi.get('/admin/dashboard'),
        adminApi.get('/admin/bookings'),
      ]);
      setDashboard(dashboardResponse.data);
      setBookings(bookingsResponse.data || []);
      setError('');
      return bookingsResponse.data || [];
    } catch (requestError) {
      setError(requestError.message || 'Unable to load dashboard.');
      return [];
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let active = true;

    (async () => {
      const latestBookings = await loadDashboard();
      if (!active) {
        return;
      }
      if (selectedBooking) {
        const refreshed = latestBookings.find((booking) => booking.bookingId === selectedBooking.bookingId);
        setSelectedBooking(refreshed || null);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const recentBookings = useMemo(
    () =>
      [...bookings]
        .sort((left, right) =>
          `${right.date} ${right.time}`.localeCompare(`${left.date} ${left.time}`),
        )
        .slice(0, 8),
    [bookings],
  );
  const previousSevenDays = useMemo(() => lastSevenDays(bookings), [bookings]);

  function openBooking(booking) {
    setSelectedBooking(booking);
    setForm({
      tablePreference: booking.tablePreference || '',
      paymentStatus: booking.paymentStatus || 'pending',
      couponCode: booking.couponCode || '',
      specialRequest: booking.specialRequest || '',
    });
  }

  async function refreshAfterAction(bookingId, closeModal = false) {
    const latestBookings = await loadDashboard();
    const refreshed = latestBookings.find((booking) => booking.bookingId === bookingId);
    setSelectedBooking(closeModal ? null : refreshed || null);
  }

  async function runAction(bookingId, action) {
    try {
      await adminApi.post(`/admin/bookings/${bookingId}/${action}`);
      toast.success(`Booking ${action.replace('-', ' ')} success.`);
      await refreshAfterAction(bookingId, action === 'cancel');
    } catch (requestError) {
      toast.error(requestError.message || `Unable to ${action} booking.`);
    }
  }

  async function saveEdits() {
    if (!selectedBooking) {
      return;
    }

    try {
      await adminApi.patch(`/admin/bookings/${selectedBooking.bookingId}`, {
        tablePreference: form.tablePreference,
        paymentStatus: form.paymentStatus,
        couponCode: form.couponCode,
        specialRequest: form.specialRequest,
      });
      toast.success('Booking updated.');
      await refreshAfterAction(selectedBooking.bookingId);
    } catch (requestError) {
      toast.error(requestError.message || 'Unable to update booking.');
    }
  }

  return (
    <div className="portal-stack">
      <section className="portal-grid portal-grid-stats">
        <PortalStat label="Total Bookings" value={dashboard?.totalBookings ?? 0} />
        <PortalStat label="Today's Bookings" value={dashboard?.todaysBookings ?? 0} tone="warm" />
        <PortalStat label="Upcoming" value={dashboard?.upcomingBookings ?? 0} />
        <PortalStat label="Revenue" value={`Rs. ${Number(dashboard?.revenue || 0).toFixed(0)}`} tone="success" />
      </section>

      {error ? <div className="portal-error">{error}</div> : null}

      <section className="portal-grid portal-grid-main">
        <TrendChart
          title="Bookings · Last 7 Days"
          points={previousSevenDays}
          valueKey="bookings"
          stroke="#8f4a00"
          fill="#f1b98c"
        />
        <TrendChart
          title="Revenue · Last 7 Days"
          points={previousSevenDays}
          valueKey="revenue"
          stroke="#2d5e34"
          fill="#8ec19a"
        />
      </section>

      <PortalCard>
        <div className="portal-section-head">
          <div>
            <p className="portal-kicker">Overview</p>
            <h2>Recent bookings</h2>
          </div>
          {loading ? <span className="portal-muted">Loading...</span> : null}
          <Link className="portal-inline-link" to="/admin/bookings">
            View all bookings
          </Link>
        </div>
        {recentBookings.length ? (
          <div className="portal-table-wrap">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Booking</th>
                  <th>Guest</th>
                  <th>Schedule</th>
                  <th>Payment</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.map((booking) => (
                  <tr key={booking.bookingId}>
                    <td>
                      <button
                        type="button"
                        className="portal-id-button"
                        onClick={() => openBooking(booking)}
                      >
                        {booking.bookingId}
                      </button>
                    </td>
                    <td>{booking.customerName}</td>
                    <td>{booking.date} at {booking.time}</td>
                    <td>{paymentLabel(booking)}</td>
                    <td>
                      <PortalStatus tone={booking.status === 'cancelled' ? 'danger' : 'default'}>
                        {booking.status}
                      </PortalStatus>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <PortalEmpty title="No bookings yet" body="Once the hosted backend starts returning reservations, they will show up here." />
        )}
      </PortalCard>

      <PortalModal
        open={Boolean(selectedBooking)}
        title={selectedBooking ? `Booking ${selectedBooking.bookingId}` : 'Booking'}
        onClose={() => setSelectedBooking(null)}
      >
        {selectedBooking ? (
          <div className="portal-stack">
            <div className="portal-detail-grid">
              <div><strong>Guest</strong><span>{selectedBooking.customerName}</span></div>
              <div><strong>Email</strong><span>{selectedBooking.email || '-'}</span></div>
              <div><strong>Phone</strong><span>{selectedBooking.phone || '-'}</span></div>
              <div><strong>Status</strong><span>{selectedBooking.status}</span></div>
              <div><strong>Table preference</strong><span>{selectedBooking.tablePreference || '-'}</span></div>
              <div><strong>Total</strong><span>Rs. {selectedBooking.totalAmount || 0}</span></div>
              <div><strong>Created</strong><span>{selectedBooking.createdAt ? new Date(selectedBooking.createdAt).toLocaleString() : '-'}</span></div>
            </div>
            <div className="portal-detail-grid">
              <PortalInput
                label="Table preference"
                value={form.tablePreference}
                onChange={(event) => setForm((current) => ({ ...current, tablePreference: event.target.value }))}
                placeholder="Indoor / Outdoor / No Preference"
              />
              <PortalSelect
                label="Payment status"
                value={form.paymentStatus}
                onChange={(event) => setForm((current) => ({ ...current, paymentStatus: event.target.value }))}
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
              </PortalSelect>
            </div>
            <PortalInput
              label="Coupon code"
              value={form.couponCode}
              onChange={(event) => setForm((current) => ({ ...current, couponCode: event.target.value }))}
              placeholder="Optional coupon"
            />
            <PortalTextarea
              label="Special request"
              rows="4"
              value={form.specialRequest}
              onChange={(event) => setForm((current) => ({ ...current, specialRequest: event.target.value }))}
              placeholder="Guest notes"
            />
            <div className="portal-action-row">
              <PortalButton onClick={saveEdits}>Save</PortalButton>
              {['pending', 'confirmed'].includes(selectedBooking.status) ? (
                <PortalButton variant="secondary" onClick={() => runAction(selectedBooking.bookingId, 'check-in')}>
                  Check in
                </PortalButton>
              ) : null}
              {['pending', 'confirmed', 'checked_in'].includes(selectedBooking.status) ? (
                <PortalButton variant="secondary" onClick={() => runAction(selectedBooking.bookingId, 'cancel')}>
                  Cancel booking
                </PortalButton>
              ) : null}
            </div>
          </div>
        ) : null}
      </PortalModal>
    </div>
  );
}
